import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Grid,
  Modal,
  Backdrop,
  Dialog,
  Collapse,
  Slider,
  TableSortLabel,
} from '@material-ui/core';
import { decode } from 'html-entities';
import CancelIcon from '@material-ui/icons/Cancel';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useState, useMemo, useCallback, useEffect } from 'react';
import SearchIcon from '@material-ui/icons/Search';
import { useHistory } from 'react-router-dom';
import { cloneDeep, keyBy } from 'lodash';

import useAuth from 'src/hooks/useAuth';
import useCampaign from 'src/hooks/useCampaign';
import useDashboardFilters from 'src/hooks/useDashboardFilters';
import { useTextStyles } from 'src/hooks/useTextStyles';
import { CampaignModal } from 'src/modals/CampaignModal';
import {
  getCampaignDetail,
  getCampaignDetailStats,
  getCampaignRows,
  getCampaignShowNames,
  getUnscoredEpisodeImpressions,
  scoreUnscoredEpisodes,
  startCampaignProcess,
  getCampaignDownloadZipHref,
} from 'src/apis/campaign';
import { getGarmScoresPerShow } from 'src/apis/garm';
import {
  CampaignDetailProps,
  CampaignDetailStatsApiResponse,
  CampaignItemProps,
  CampaignRowsView,
  CampaignRowsAlignmentFilter,
  CampaignRowsSortBy,
  CampaignRowsSortOrder,
} from 'src/types/campaign';
import ButtonDropdown, { OptionProps } from 'src/components/ButtonDropdown';
import { TableWrapper, HeaderCell, Footer } from '../dashboard/garm/components';
import Spinner from 'src/components/Spinner';
import Button from 'src/components/Button';
import { useCreateDashboard, useUpdateDashboard } from '../dashboard/hooks';
import { DashboardType, Dashboard, IGarmScoreDto } from 'src/types';
import {
  getGarmDashboards,
  formatPodcastRatingNumber,
  formatPodcastRating,
} from 'src/utils';
import TextInput from 'src/components/TextInput';
import { DashboardList } from 'src/components/sidebar';
import { useDashboardData } from '../dashboard/garm/hooks';
import useDashboardCategories from 'src/hooks/useDashboardCategories';
import GarmArticleContent from '../content-screen/GARMArticleContent';
import useNotification from 'src/hooks/useNotification';

import { NoCampaign } from './NoCampaign';
import CampaignItemEpisodeRow from './CampaignItemEpisodeRow';
import { Row } from '../dashboard/garm/components';
import { useCampaignsStyles } from './campaigns.styles';
import {
  SEARCH_GROUPS,
  SHOW_ONLY_SEARCH_GROUPS,
  CAMPAIGN_ROWS_ALIGNMENT_OPTIONS,
} from './campaigns.constants';
import {
  getCampaignReportFromStats,
  formatNumber,
  formatTotalImpressionsFromRaw,
} from './campaignReport';
import {
  mapCampaignRowsToItems,
  parseCampaignRowsPayload,
} from './campaignRowsMappers';

const modalStyle = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  width: 350,
  bgcolor: 'background.paper',
  border: '2px solid #e0e0e0',
  borderRadius: '20px 0 0 20px',
  boxShadow: 24,
  pt: 2,
  px: 2,
  pb: 3,
};

const createNewModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 450,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

const SCORING_EPISODES_SLIDER_MAX = 2000;

const getSliderChangeValue = (value: number | number[]): number =>
  Array.isArray(value) ? value[0] : value;

const groupByToRowsView = (groupBy: string): CampaignRowsView =>
  groupBy === 'episode' ? 'episodes' : 'shows';

function mapRowsPayload(
  res: unknown,
  view: CampaignRowsView
): { items: CampaignItemProps[]; total: number } {
  const { rows, total } = parseCampaignRowsPayload(res);
  return {
    items: mapCampaignRowsToItems(view, rows),
    total,
  };
}

function campaignTableColumnToSortBy(
  column: string,
  groupValue: string
): CampaignRowsSortBy | null {
  if (column === 'Impressions') return 'impressions';
  if (column === 'Podcast Title') return 'show_title';
  if (column === 'Episode') return 'episode_title';
  if (column === 'Aligned') {
    return groupValue === 'episode' ? 'episode_alignment' : 'show_alignment';
  }
  return null;
}

export default function Campaigns() {
  const classes = useCampaignsStyles();
  const textClasses = useTextStyles();
  const { handleError, handleSuccess } = useNotification();
  const { organization } = useAuth();
  const { selectedCampaignId, openCreateModal, setOpenCreateModal } =
    useCampaign();
  const history = useHistory();
  const { fetchAllShowNamesDashboard } = useDashboardData();
  const [currentCampaign, setCurrentCampaign] =
    useState<Nullable<IGarmScoreDto>>(null);
  const [currentShow, setCurrentShow] = useState<Nullable<string>>(null);
  const [currentEpisode, setCurrentEpisode] = useState<Nullable<string>>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [startingProcess, setStartingProcess] = useState(false);
  const [refreshingDetails, setRefreshingDetails] = useState(false);
  const [itemsLoading, setItemsLoading] = useState<boolean>(false);
  const [details, setDetails] = useState<CampaignDetailProps | null>(null);
  const [campaignStats, setCampaignStats] =
    useState<CampaignDetailStatsApiResponse | null>(null);
  const [campaignStatsStatus, setCampaignStatsStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [searchText, setSearchText] = useState<string>('');
  const [group, setGroup] = useState<OptionProps>(SEARCH_GROUPS[0]);

  // Show & Episode Alignment: match create-campaign "Show & Episode Alignment" (alignmentRequired).
  const isShowAndEpisodeAlignment = details?.alignmentRequired === true;
  const availableGroups = isShowAndEpisodeAlignment
    ? SEARCH_GROUPS
    : SHOW_ONLY_SEARCH_GROUPS;

  // Force group to 'show' when not Show & Episode Alignment
  useEffect(() => {
    if (!isShowAndEpisodeAlignment && group.value === 'episode') {
      setGroup(SHOW_ONLY_SEARCH_GROUPS[0]);
    }
  }, [isShowAndEpisodeAlignment, group.value]);
  const { garmScoreColumns, selectDashboard } = useDashboardFilters();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<CampaignItemProps[]>([]);
  const [showItems, setShowItems] = useState<CampaignItemProps[]>([]);
  const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
  const [alignmentFilterOption, setAlignmentFilterOption] =
    useState<OptionProps>(CAMPAIGN_ROWS_ALIGNMENT_OPTIONS[0]);
  const [rowsSortBy, setRowsSortBy] =
    useState<CampaignRowsSortBy>('impressions');
  const [rowsSortOrder, setRowsSortOrder] =
    useState<CampaignRowsSortOrder>('desc');
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [createNewModal, setCreateNewModal] = useState<boolean>(false);
  const [submitScoringModalOpen, setSubmitScoringModalOpen] = useState(false);
  const [scoringEpisodesSlider, setScoringEpisodesSlider] = useState(0);
  const [scoringAuthorizationCode, setScoringAuthorizationCode] = useState('');
  const [submitScoringSubmitting, setSubmitScoringSubmitting] = useState(false);
  const [unscoredEpisodeImpressions, setUnscoredEpisodeImpressions] = useState<
    number[]
  >([]);
  const [newDashboardName, setNewDashboardName] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    showMatch: true,
    episodeMatch: true,
    top10Shows: true,
    top10Episodes: true,
    top5IabCategories: true,
    top5Genres: true,
    episodeAlignment: true,
    showAlignment: true,
    episodeAlignmentTop10Misaligned: true,
    episodeAlignmentTop10Aligned: true,
    showAlignmentTop10Misaligned: true,
    showAlignmentTop10Aligned: true,
  });

  // Campaign rows: coerce invalid sort when switching show / episode view
  useEffect(() => {
    if (group.value === 'show' && rowsSortBy === 'episode_title') {
      setRowsSortBy('impressions');
      setRowsSortOrder('desc');
      return;
    }
    if (group.value === 'show' && rowsSortBy === 'episode_alignment') {
      setRowsSortBy('show_alignment');
      return;
    }
    if (group.value === 'episode' && rowsSortBy === 'show_alignment') {
      setRowsSortBy('episode_alignment');
    }
  }, [group.value, rowsSortBy]);

  useEffect(() => {
    setPage(0);
  }, [
    alignmentFilterOption.value,
    rowsSortBy,
    rowsSortOrder,
    group.value,
    searchText,
  ]);

  const toggleSection = (key: string) =>
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const { onCreateDashboard } = useCreateDashboard();
  const { onUpdateDashboard } = useUpdateDashboard();
  const { filterMapInit } = useDashboardCategories();
  const firstLoading = details?.id !== selectedCampaignId;

  const campaignReport = useMemo(
    () => getCampaignReportFromStats(campaignStats),
    [campaignStats]
  );

  /** From `scored_episodes_stats` via `getCampaignReportFromStats` → `episodeUnscored`. */
  const unscoredScoringTotals = useMemo(() => {
    const episodes = campaignReport?.episodeUnscored.uniqueEpisodes ?? 0;
    const impressions = campaignReport?.episodeUnscored.impressions ?? 0;
    return { episodes, impressions };
  }, [campaignReport]);

  const scoringEpisodesSliderMax = Math.min(
    SCORING_EPISODES_SLIDER_MAX,
    Math.max(unscoredScoringTotals.episodes, 0)
  );

  /** Sum of the first N impression values in `unscoredEpisodeImpressions` (ASC), N = episodes slider. */
  const unscoredImpressionsToScore = useMemo(() => {
    const n = Math.min(
      Math.max(0, scoringEpisodesSlider),
      unscoredEpisodeImpressions.length
    );
    return unscoredEpisodeImpressions
      .slice(0, n)
      .reduce((sum, x) => sum + x, 0);
  }, [scoringEpisodesSlider, unscoredEpisodeImpressions]);

  const riskFilters = cloneDeep(filterMapInit);

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCampaignRowsSort = (field: CampaignRowsSortBy) => {
    if (rowsSortBy === field) {
      setRowsSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setRowsSortBy(field);
      setRowsSortOrder(field === 'impressions' ? 'desc' : 'asc');
    }
  };

  const fetchCampaignDetails = useCallback(
    async (id: string, enableLoading?: boolean) => {
      if (enableLoading) {
        setLoading(true);
      }
      setCampaignStatsStatus('loading');
      setCampaignStats(null);

      try {
        await getCampaignShowNames(id);
        const response = await getCampaignDetail(id);
        setDetails(response);

        if (response.status === 'pending') {
          setCampaignStats(null);
          setCampaignStatsStatus('idle');
        } else {
          const statsResponse = await getCampaignDetailStats(id).catch(
            () => null
          );
          if (statsResponse?.status === 'success') {
            setCampaignStats(statsResponse);
            setCampaignStatsStatus('success');
          } else {
            setCampaignStats(null);
            setCampaignStatsStatus('error');
          }
        }
        setLoading(false);
      } catch (error) {
        setDetails(null);
        setCampaignStats(null);
        setCampaignStatsStatus('error');
        setLoading(false);
      }
    },
    []
  );

  const handleRefreshCampaignDetails = useCallback(async () => {
    if (!selectedCampaignId) {
      return;
    }
    setRefreshingDetails(true);
    try {
      await fetchCampaignDetails(selectedCampaignId, false);
    } finally {
      setRefreshingDetails(false);
    }
  }, [selectedCampaignId, fetchCampaignDetails]);

  const handleStartCampaignProcess = useCallback(async () => {
    if (!selectedCampaignId) {
      return;
    }
    setStartingProcess(true);
    try {
      const res = await startCampaignProcess({
        campaign_id: selectedCampaignId,
      });
      if (res.status === 'success') {
        handleSuccess(res.message || 'Campaign process started');
        await fetchCampaignDetails(selectedCampaignId, false);
      } else {
        handleError({ message: res.message }, res.message);
      }
    } catch (error) {
      handleError(error, 'Failed to start campaign process');
    } finally {
      setStartingProcess(false);
    }
  }, [selectedCampaignId, fetchCampaignDetails, handleError, handleSuccess]);

  const closeSubmitScoringModal = useCallback(() => {
    setSubmitScoringModalOpen(false);
    setScoringAuthorizationCode('');
    setUnscoredEpisodeImpressions([]);
  }, []);

  useEffect(() => {
    if (!submitScoringModalOpen || !selectedCampaignId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const impressions = await getUnscoredEpisodeImpressions(
          selectedCampaignId
        );
        const sortedAsc = [...impressions].sort((a, b) => b - a);
        if (!cancelled) {
          setUnscoredEpisodeImpressions(sortedAsc);
        }
      } catch {
        if (!cancelled) {
          setUnscoredEpisodeImpressions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submitScoringModalOpen, selectedCampaignId]);

  const openSubmitScoringModal = useCallback(() => {
    const totalEp = campaignReport?.episodeUnscored.uniqueEpisodes ?? 0;
    setSubmitScoringModalOpen(true);
    setScoringEpisodesSlider(Math.min(totalEp, SCORING_EPISODES_SLIDER_MAX));
    setScoringAuthorizationCode('');
  }, [campaignReport]);

  const handleSubmitScoringProcess = useCallback(async () => {
    if (!selectedCampaignId) {
      return;
    }
    const authCode = scoringAuthorizationCode.trim();
    if (!authCode) {
      handleError(
        { message: 'Authorization code is required' },
        'Authorization code is required'
      );
      return;
    }
    setSubmitScoringSubmitting(true);
    try {
      const res = await scoreUnscoredEpisodes({
        campaign_id: selectedCampaignId,
        auth_code: authCode,
        episodes_count: scoringEpisodesSlider,
      });
      if (res.status === 'success') {
        handleSuccess(res.message || 'Scoring process submitted');
        closeSubmitScoringModal();
        await fetchCampaignDetails(selectedCampaignId, false);
      } else {
        handleError(
          { message: res.message },
          res.message || 'Failed to submit scoring process'
        );
      }
    } catch (error) {
      handleError(error, 'Failed to submit scoring process');
    } finally {
      setSubmitScoringSubmitting(false);
    }
  }, [
    selectedCampaignId,
    scoringAuthorizationCode,
    scoringEpisodesSlider,
    fetchCampaignDetails,
    closeSubmitScoringModal,
    handleError,
    handleSuccess,
  ]);

  useEffect(() => {
    if (selectedCampaignId) {
      fetchCampaignDetails(selectedCampaignId, firstLoading);
    } else {
      setDetails(null);
      setCampaignStats(null);
      setCampaignStatsStatus('idle');
    }
  }, [selectedCampaignId, firstLoading, fetchCampaignDetails]);

  const fetchCampaignItems = useCallback(
    async (
      id: string,
      pageNum: number,
      pageSizeArg: number,
      groupBy: string,
      search: string
    ) => {
      setItemsLoading(true);
      try {
        const view = groupByToRowsView(groupBy);
        const alignmentFilter: CampaignRowsAlignmentFilter | undefined =
          alignmentFilterOption.value !== ''
            ? (alignmentFilterOption.value as CampaignRowsAlignmentFilter)
            : undefined;
        const response = await getCampaignRows(id, {
          view,
          page: pageNum,
          page_size: pageSizeArg,
          search: search || undefined,
          alignment_filter: alignmentFilter,
          sort_by: rowsSortBy,
          sort_order: rowsSortOrder,
        });
        const { items: rowItems, total } = mapRowsPayload(response, view);
        if (groupBy === 'episode') {
          setItems(rowItems);
        } else {
          setShowItems(rowItems);
        }
        setTotalItemsCount(total);
        setItemsLoading(false);
      } catch (error) {
        setItemsLoading(false);
      }
    },
    [alignmentFilterOption.value, rowsSortBy, rowsSortOrder]
  );

  useEffect(() => {
    if (selectedCampaignId) {
      fetchCampaignItems(
        selectedCampaignId,
        page,
        pageSize,
        group.value,
        searchText
      );
    }
  }, [
    selectedCampaignId,
    page,
    pageSize,
    group,
    searchText,
    fetchCampaignItems,
  ]);

  const garmDashboards = useMemo(
    () => (organization ? getGarmDashboards(organization) : []),
    [organization]
  );

  const columns = useMemo(() => {
    if (group.value === 'episode' && isShowAndEpisodeAlignment) {
      return [
        'Episode',
        'Podcast Title',
        'Aligned',
        'Impressions',
        ...garmScoreColumns,
      ];
    }
    return ['Podcast Title', 'Aligned', 'Impressions', ...garmScoreColumns];
  }, [group, garmScoreColumns, isShowAndEpisodeAlignment]);

  const handleNewDashboard = async (name: string) => {
    if (!selectedCampaignId || !name) {
      return;
    }
    const showNames = await getCampaignShowNames(selectedCampaignId);

    const garmFilters = keyBy(
      Object.values(riskFilters).map((riskFilter) => ({
        dashboardFilterKey: riskFilter.dashboardFilterKey,
        enabled: riskFilter.dashboardFilterKey === 'audience' ? false : true,
        value: riskFilter.dashboardFilterKey === 'audience' ? [0, 5] : [0, 3],
      })),
      'dashboardFilterKey'
    );

    const dashboard = await onCreateDashboard({
      type: DashboardType.GARM,
      keywordsList: showNames,
      garmFilters: {
        ...garmFilters,
        publishersList: [],
        iabCategories: [],
        genresList: [],
      },
      exceptionFilterId: null,
      name,
    });

    if (dashboard) {
      selectDashboard(dashboard);
      history.push('/dashboard');
    }
  };

  const handleSelectDashboard = async (selectedDashboard: Dashboard) => {
    if (selectedDashboard && selectedCampaignId) {
      setOpenModal(false);
      const showNames = await getCampaignShowNames(selectedCampaignId);
      const existingNames = await fetchAllShowNamesDashboard(selectedDashboard);

      const garmFiltersData = {
        name: selectedDashboard.name,
        type: DashboardType.GARM,
        keywordsList: [...existingNames, ...showNames],
        garmFilters: selectedDashboard.garmFilters,
        exceptionFilterId: selectedDashboard.exceptionFilterId || null,
      };
      await onUpdateDashboard(selectedDashboard.id, garmFiltersData);
      selectDashboard({
        ...selectedDashboard,
        keywordsList: [...existingNames, ...showNames],
      });
      history.push('/dashboard');
    }
  };

  const fetchGarmScoresPerShow = async (
    showName: string,
    episodeName: string
  ) => {
    try {
      setLoading(true);
      const data = await getGarmScoresPerShow(showName, {});
      const episodes = data.data.map((item: any) => {
        const newItem = {
          ...item,
          Listeners: formatPodcastRating(item.Listeners),
        };

        return newItem;
      });
      const episode = episodes.find(
        (item) => item['Episode Title'] === episodeName
      );

      if (episode) {
        setCurrentCampaign(episode);
        setCurrentShow(showName);
      }
    } catch (error) {
      setCurrentCampaign(null);
      setCurrentShow(null);
    } finally {
      setLoading(false);
    }
  };

  if (!organization) {
    return null;
  }

  return (
    <>
      {selectedCampaignId ? (
        <>
          {loading || !details ? (
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              className={classes.container}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box className={classes.container}>
              <Box className={classes.header}>
                <Box
                  display='flex'
                  flexDirection='row'
                  justifyContent='space-between'
                  alignItems='flex-start'
                  style={{ gap: 16 }}
                >
                  <Box flex={1} minWidth={0}>
                    <Typography
                      className={textClasses.xxlBold}
                      style={{ color: '#344054' }}
                    >
                      {details.name}
                    </Typography>
                    <Typography
                      className={textClasses.baseRegular}
                      style={{ color: '#98A2B3', marginTop: 8 }}
                    >
                      {details.description}
                    </Typography>
                  </Box>
                  <Box
                    display='flex'
                    flexDirection='row'
                    alignItems='center'
                    style={{ gap: 8 }}
                    flexShrink={0}
                  >
                    {details.status === 'processing' && (
                      <Box
                        display='flex'
                        alignItems='center'
                        style={{ gap: 8, padding: '8px 12px' }}
                      >
                        <CircularProgress
                          size={18}
                          style={{ color: '#5EA1F2' }}
                        />
                        <Typography
                          className={textClasses.smRegular}
                          style={{ color: '#475467' }}
                        >
                          Campaign is processing…
                        </Typography>
                      </Box>
                    )}
                    <Button
                      disabled={refreshingDetails || startingProcess}
                      className={classes.addBtn}
                      onClick={handleRefreshCampaignDetails}
                    >
                      {refreshingDetails ? <Spinner size={12} /> : 'Refresh'}
                    </Button>
                    {details.status === 'pending' && (
                      <Button
                        disabled={startingProcess || refreshingDetails}
                        className={classes.addBtn}
                        onClick={handleStartCampaignProcess}
                      >
                        {startingProcess ? (
                          <Spinner size={12} />
                        ) : (
                          'Start campaign process'
                        )}
                      </Button>
                    )}
                    {details.status === 'completed' &&
                      isShowAndEpisodeAlignment && (
                        <Button
                          disabled={refreshingDetails}
                          className={classes.addBtn}
                          onClick={openSubmitScoringModal}
                        >
                          Submit Scoring
                        </Button>
                      )}
                  </Box>
                </Box>
                <Box
                  display='flex'
                  flexWrap='wrap'
                  style={{ gap: 6, marginTop: 16 }}
                >
                  <Typography
                    className={textClasses.smRegular}
                    style={{ color: '#475467' }}
                  >
                    <b>Reporting date:</b>{' '}
                    {details.createdAt
                      ? new Date(details.createdAt).toLocaleDateString()
                      : '—'}
                  </Typography>
                  <Typography
                    className={textClasses.smRegular}
                    style={{ color: '#475467' }}
                  >
                    <b>Campaign start:</b>{' '}
                    {details.campaignStart ?? details.campaign_start ?? '—'}
                  </Typography>
                  <Typography
                    className={textClasses.smRegular}
                    style={{ color: '#475467' }}
                  >
                    <b>Campaign end:</b>{' '}
                    {details.campaignEnd ?? details.campaign_end ?? '—'}
                  </Typography>
                  {details.status === 'completed' && (
                    <Typography
                      className={textClasses.smRegular}
                      style={{ color: '#475467' }}
                    >
                      <b>Total impressions:</b>{' '}
                      {campaignReport != null
                        ? formatNumber(campaignReport.totalImpressions)
                        : formatTotalImpressionsFromRaw(
                            details.detailedStats?.overall_stats
                              ?.total_impressions ??
                              campaignStats?.overall_stats?.total_impressions
                          )}
                    </Typography>
                  )}
                </Box>
              </Box>
              {details.status === 'completed' && (
                <Box className={classes.reportStatsContainer}>
                  {campaignStatsStatus === 'error' && (
                    <Box
                      display='flex'
                      flexDirection='column'
                      style={{ marginBottom: 12 }}
                    >
                      <Typography
                        className={textClasses.smRegular}
                        style={{ color: '#B42318' }}
                      >
                        Campaign metrics could not be loaded. Try Refresh.
                      </Typography>
                    </Box>
                  )}

                  <Box className={classes.reportSectionsGrid}>
                    {/* Show Matched / Unmatched */}
                    {group.value === 'show' && (
                      <Box className={classes.reportSection}>
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() => toggleSection('showMatch')}
                        >
                          Barometer Show Match
                          {expandedSections.showMatch ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse in={expandedSections.showMatch}>
                          <Box className={classes.reportSectionBody}>
                            <Box className={classes.statBlock}>
                              <Typography className={classes.statBlockTitle}>
                                Matched
                              </Typography>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Matched Unique Shows
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.showMatched.uniqueShows ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {campaignReport?.showMatched
                                    .uniqueShowsPercent ?? 0}
                                  %
                                </span>
                              </Box>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Matched Unique Show Impressions
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.showMatched.impressions ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {campaignReport?.showMatched
                                    .impressionsPercent ?? 0}
                                  %
                                </span>
                              </Box>
                            </Box>
                            <Box className={classes.statBlock}>
                              <Typography className={classes.statBlockTitle}>
                                Unmatched
                              </Typography>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Unmatched Unique Shows
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.showUnmatched.uniqueShows ??
                                      0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {campaignReport?.showUnmatched
                                    .uniqueShowsPercent ?? 0}
                                  %
                                </span>
                              </Box>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Unmatched Unique Show Impressions
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.showUnmatched.impressions ??
                                      0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {campaignReport?.showUnmatched
                                    .impressionsPercent ?? 0}
                                  %
                                </span>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Episode Matched / Unmatched / Scored / Unscored */}
                    {group.value === 'episode' && (
                      <Box className={classes.reportSection}>
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() => toggleSection('episodeMatch')}
                        >
                          Barometer Episode Match & Score
                          {expandedSections.episodeMatch ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse in={expandedSections.episodeMatch}>
                          <Box className={classes.reportSectionBody}>
                            <Box className={classes.statBlock}>
                              <Typography className={classes.statBlockTitle}>
                                Matched
                              </Typography>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Matched Unique Episodes
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeMatched
                                      .uniqueEpisodes ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {campaignReport?.episodeMatched
                                    .uniqueEpisodesPercent ?? 0}
                                  %
                                </span>
                              </Box>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Matched Unique Episode Impressions
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeMatched
                                      .impressions ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {campaignReport?.episodeMatched
                                    .impressionsPercent ?? 0}
                                  %
                                </span>
                              </Box>
                            </Box>
                            <Box className={classes.statBlock}>
                              <Typography className={classes.statBlockTitle}>
                                Unmatched
                              </Typography>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Unmatched Unique Episodes
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeUnmatched
                                      .uniqueEpisodes ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {campaignReport?.episodeUnmatched
                                    .uniqueEpisodesPercent ?? 0}
                                  %
                                </span>
                              </Box>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Unmatched Unique Episode Impressions
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeUnmatched
                                      .impressions ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {campaignReport?.episodeUnmatched
                                    .impressionsPercent ?? 0}
                                  %
                                </span>
                              </Box>
                            </Box>
                            <Box className={classes.statBlock}>
                              <Typography className={classes.statBlockTitle}>
                                Scored
                              </Typography>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Scored Unique Episodes
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeScored
                                      .uniqueEpisodes ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.episodeScored
                                      .uniqueEpisodesPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Scored Unique Episode Impressions
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeScored.impressions ??
                                      0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.episodeScored
                                      .impressionsPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                            </Box>
                            <Box className={classes.statBlock}>
                              <Typography className={classes.statBlockTitle}>
                                Unscored
                              </Typography>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Unscored Unique Episodes
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeUnscored
                                      .uniqueEpisodes ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.episodeUnscored
                                      .uniqueEpisodesPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Unscored Unique Episode Impressions
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeUnscored
                                      .impressions ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.episodeUnscored
                                      .impressionsPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Top 10 Shows by Impression */}
                    {group.value === 'show' && (
                      <Box className={classes.reportSection}>
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() => toggleSection('top10Shows')}
                        >
                          Top 10 Shows by Impression
                          {expandedSections.top10Shows ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse in={expandedSections.top10Shows}>
                          <Box className={classes.reportSectionBody}>
                            <table className={classes.top10Table}>
                              <tbody>
                                {(
                                  campaignReport?.top10ShowsByImpression ?? []
                                ).map((item, idx) => (
                                  <tr key={idx} className={classes.top10Row}>
                                    <td className={classes.top10Rank}>
                                      {idx + 1}.
                                    </td>
                                    <td className={classes.top10Name}>
                                      {item.name}
                                    </td>
                                    <td className={classes.top10Impressions}>
                                      {formatNumber(item.impressions)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Top 10 Episodes by Impression */}
                    {group.value === 'episode' && (
                      <Box className={classes.reportSection}>
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() => toggleSection('top10Episodes')}
                        >
                          Top 10 Episodes by Impression
                          {expandedSections.top10Episodes ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse in={expandedSections.top10Episodes}>
                          <Box className={classes.reportSectionBody}>
                            <table className={classes.top10Table}>
                              <tbody>
                                {(
                                  campaignReport?.top10EpisodesByImpression ??
                                  []
                                ).map((item, idx) => (
                                  <tr key={idx} className={classes.top10Row}>
                                    <td className={classes.top10Rank}>
                                      {idx + 1}.
                                    </td>
                                    <td className={classes.top10Name}>
                                      {item.name}
                                    </td>
                                    <td className={classes.top10Impressions}>
                                      {formatNumber(item.impressions)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Top 5 Episode IAB Categories by Impression */}
                    {group.value === 'episode' && (
                      <Box className={classes.reportSection}>
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() => toggleSection('top5IabCategories')}
                        >
                          Top 5 Episode IAB Categories by Impression
                          {expandedSections.top5IabCategories ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse in={expandedSections.top5IabCategories}>
                          <Box className={classes.reportSectionBody}>
                            {(campaignReport?.top5IabCategoriesByImpression
                              ?.length ?? 0) > 0 ? (
                              <table className={classes.top10Table}>
                                <tbody>
                                  {(
                                    campaignReport?.top5IabCategoriesByImpression ??
                                    []
                                  ).map((item, idx) => (
                                    <tr key={idx} className={classes.top10Row}>
                                      <td className={classes.top10Rank}>
                                        {idx + 1}.
                                      </td>
                                      <td className={classes.top10Name}>
                                        {item}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <Typography className={classes.emptyList}>
                                — —
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Top 5 Genres by Impression */}
                    {group.value === 'show' && (
                      <Box className={classes.reportSection}>
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() => toggleSection('top5Genres')}
                        >
                          Top 5 Genres by Impression
                          {expandedSections.top5Genres ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse in={expandedSections.top5Genres}>
                          <Box className={classes.reportSectionBody}>
                            {(campaignReport?.top5GenresByImpression?.length ??
                              0) > 0 ? (
                              <table className={classes.top10Table}>
                                <tbody>
                                  {(
                                    campaignReport?.top5GenresByImpression ?? []
                                  ).map((item, idx) => (
                                    <tr key={idx} className={classes.top10Row}>
                                      <td className={classes.top10Rank}>
                                        {idx + 1}.
                                      </td>
                                      <td className={classes.top10Name}>
                                        {item}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <Typography className={classes.emptyList}>
                                — —
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Episode Alignment Statistics */}
                    {group.value === 'episode' && (
                      <Box className={classes.reportSection}>
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() => toggleSection('episodeAlignment')}
                        >
                          Episode Alignment Statistics
                          {expandedSections.episodeAlignment ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse in={expandedSections.episodeAlignment}>
                          <Box className={classes.reportSectionBody}>
                            <Box className={classes.statBlock}>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Scored Unique Episodes Impressions
                                  Aligned
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeAlignment
                                      .impressionsAligned ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.episodeAlignment
                                      .impressionsAlignedPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Scored Unique Episodes Aligned
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeAlignment
                                      .episodesAligned ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.episodeAlignment
                                      .episodesAlignedPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                            </Box>
                            <Box className={classes.statBlock}>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Scored Unique Episodes Impressions
                                  Misaligned
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeAlignment
                                      .impressionsMisaligned ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.episodeAlignment
                                      .impressionsMisalignedPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Scored Unique Episodes Misaligned
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.episodeAlignment
                                      .episodesMisaligned ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.episodeAlignment
                                      .episodesMisalignedPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Top 10 Misaligned Episodes by Impression (alignment) */}
                    {group.value === 'episode' && (
                      <Box
                        className={`${classes.reportSection} ${classes.reportSectionFullWidth}`}
                      >
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() =>
                            toggleSection('episodeAlignmentTop10Misaligned')
                          }
                        >
                          Top 10 Misaligned Episodes by Impression
                          {expandedSections.episodeAlignmentTop10Misaligned ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse
                          in={expandedSections.episodeAlignmentTop10Misaligned}
                        >
                          <Box className={classes.reportSectionBody}>
                            {(campaignReport?.episodeAlignment
                              .top10MisalignedEpisodes?.length ?? 0) > 0 ? (
                              <table className={classes.top10Table}>
                                <tbody>
                                  {(
                                    campaignReport?.episodeAlignment
                                      .top10MisalignedEpisodes ?? []
                                  ).map((item, idx) => (
                                    <tr key={idx} className={classes.top10Row}>
                                      <td className={classes.top10Rank}>
                                        {idx + 1}.
                                      </td>
                                      <td className={classes.top10ShowTitle}>
                                        {item.showTitle?.trim()
                                          ? item.showTitle
                                          : '—'}
                                      </td>
                                      <td className={classes.top10Name}>
                                        {item.name}
                                      </td>
                                      <td
                                        className={classes.top10ViolationReason}
                                      >
                                        <div
                                          className={
                                            classes.top10ViolationReasonText
                                          }
                                          title={
                                            item.violationReason?.trim() ||
                                            undefined
                                          }
                                        >
                                          {item.violationReason?.trim()
                                            ? item.violationReason
                                            : '—'}
                                        </div>
                                      </td>
                                      <td className={classes.top10Impressions}>
                                        {formatNumber(item.impressions)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <Typography className={classes.emptyList}>
                                — —
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Top 10 Aligned Episodes by Impression (alignment) */}
                    {group.value === 'episode' && (
                      <Box className={classes.reportSection}>
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() =>
                            toggleSection('episodeAlignmentTop10Aligned')
                          }
                        >
                          Top 10 Aligned Episodes by Impression
                          {expandedSections.episodeAlignmentTop10Aligned ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse
                          in={expandedSections.episodeAlignmentTop10Aligned}
                        >
                          <Box className={classes.reportSectionBody}>
                            {(campaignReport?.episodeAlignment
                              .top10AlignedEpisodes?.length ?? 0) > 0 ? (
                              <table className={classes.top10Table}>
                                <tbody>
                                  {(
                                    campaignReport?.episodeAlignment
                                      .top10AlignedEpisodes ?? []
                                  ).map((item, idx) => (
                                    <tr key={idx} className={classes.top10Row}>
                                      <td className={classes.top10Rank}>
                                        {idx + 1}.
                                      </td>
                                      <td className={classes.top10ShowTitle}>
                                        {item.showTitle?.trim()
                                          ? item.showTitle
                                          : '—'}
                                      </td>
                                      <td className={classes.top10Name}>
                                        {item.name}
                                      </td>
                                      <td className={classes.top10Impressions}>
                                        {formatNumber(item.impressions)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <Typography className={classes.emptyList}>
                                — —
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Show Alignment Statistics */}
                    {group.value === 'show' && (
                      <Box className={classes.reportSection}>
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() => toggleSection('showAlignment')}
                        >
                          Show Alignment Statistics
                          {expandedSections.showAlignment ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse in={expandedSections.showAlignment}>
                          <Box className={classes.reportSectionBody}>
                            <Box className={classes.statBlock}>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Matched Unique Shows Impressions
                                  Aligned
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.showAlignment
                                      .impressionsAligned ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.showAlignment
                                      .impressionsAlignedPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Matched Unique Shows Aligned
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.showAlignment
                                      .showsAligned ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.showAlignment
                                      .showsAlignedPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                            </Box>
                            <Box className={classes.statBlock}>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Matched Unique Shows Impressions
                                  Misaligned
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.showAlignment
                                      .impressionsMisaligned ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.showAlignment
                                      .impressionsMisalignedPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                              <Box className={classes.statRow}>
                                <span className={classes.statRowLabel}>
                                  Barometer Matched Unique Shows Misaligned
                                </span>
                                <span className={classes.statRowValue}>
                                  {formatNumber(
                                    campaignReport?.showAlignment
                                      .showsMisaligned ?? 0
                                  )}
                                </span>
                                <span className={classes.statRowPercent}>
                                  {(
                                    campaignReport?.showAlignment
                                      .showsMisalignedPercent ?? 0
                                  ).toFixed(2)}
                                  %
                                </span>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Top 10 Misaligned Shows by Impression (alignment) */}
                    {group.value === 'show' && (
                      <Box
                        className={`${classes.reportSection} ${classes.reportSectionFullWidth}`}
                      >
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() =>
                            toggleSection('showAlignmentTop10Misaligned')
                          }
                        >
                          Top 10 Misaligned Shows by Impression
                          {expandedSections.showAlignmentTop10Misaligned ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse
                          in={expandedSections.showAlignmentTop10Misaligned}
                        >
                          <Box className={classes.reportSectionBody}>
                            {(campaignReport?.showAlignment.top10MisalignedShows
                              ?.length ?? 0) > 0 ? (
                              <table className={classes.top10Table}>
                                <tbody>
                                  {(
                                    campaignReport?.showAlignment
                                      .top10MisalignedShows ?? []
                                  ).map((item, idx) => (
                                    <tr key={idx} className={classes.top10Row}>
                                      <td className={classes.top10Rank}>
                                        {idx + 1}.
                                      </td>
                                      <td className={classes.top10Name}>
                                        {item.name}
                                      </td>
                                      <td
                                        className={classes.top10ViolationReason}
                                      >
                                        <div
                                          className={
                                            classes.top10ViolationReasonText
                                          }
                                          title={
                                            item.violationReason?.trim() ||
                                            undefined
                                          }
                                        >
                                          {item.violationReason?.trim()
                                            ? item.violationReason
                                            : '—'}
                                        </div>
                                      </td>
                                      <td className={classes.top10Impressions}>
                                        {formatNumber(item.impressions)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <Typography className={classes.emptyList}>
                                — —
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Top 10 Aligned Shows by Impression (alignment) */}
                    {group.value === 'show' && (
                      <Box className={classes.reportSection}>
                        <Box
                          className={classes.reportSectionHeader}
                          onClick={() =>
                            toggleSection('showAlignmentTop10Aligned')
                          }
                        >
                          Top 10 Aligned Shows by Impression
                          {expandedSections.showAlignmentTop10Aligned ? (
                            <ExpandLessIcon fontSize='small' />
                          ) : (
                            <ExpandMoreIcon fontSize='small' />
                          )}
                        </Box>
                        <Collapse
                          in={expandedSections.showAlignmentTop10Aligned}
                        >
                          <Box className={classes.reportSectionBody}>
                            {(campaignReport?.showAlignment.top10AlignedShows
                              ?.length ?? 0) > 0 ? (
                              <table className={classes.top10Table}>
                                <tbody>
                                  {(
                                    campaignReport?.showAlignment
                                      .top10AlignedShows ?? []
                                  ).map((item, idx) => (
                                    <tr key={idx} className={classes.top10Row}>
                                      <td className={classes.top10Rank}>
                                        {idx + 1}.
                                      </td>
                                      <td className={classes.top10Name}>
                                        {item.name}
                                      </td>
                                      <td className={classes.top10Impressions}>
                                        {formatNumber(item.impressions)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <Typography className={classes.emptyList}>
                                — —
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              <Box className={classes.options}>
                <TextField
                  InputLabelProps={{
                    shrink: true,
                    style: { fontWeight: 'normal' },
                  }}
                  InputProps={{
                    placeholder: 'Search campaigns...',
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon
                          htmlColor='#858591'
                          style={{ fontSize: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  className={classes.searchBar}
                />
                <ButtonDropdown
                  id='search-group'
                  selectedOption={group}
                  options={availableGroups}
                  handleSelect={setGroup}
                />
                <ButtonDropdown
                  id='campaign-rows-alignment-filter'
                  selectedOption={alignmentFilterOption}
                  options={CAMPAIGN_ROWS_ALIGNMENT_OPTIONS}
                  handleSelect={setAlignmentFilterOption}
                />
                <Button
                  className={classes.addBtn}
                  onClick={() => {
                    setOpenModal(true);
                  }}
                >
                  + Add To
                </Button>
                <Button
                  className={classes.addBtn}
                  onClick={() => {
                    setCreateNewModal(true);
                  }}
                >
                  + Create New
                </Button>
                <Button
                  component={selectedCampaignId ? 'a' : 'button'}
                  className={classes.downloadBtn}
                  disabled={!selectedCampaignId}
                  {...(selectedCampaignId
                    ? {
                        href: getCampaignDownloadZipHref(selectedCampaignId),
                      }
                    : {})}
                >
                  Download
                </Button>
              </Box>
              <Box className={classes.items}>
                <TableContainer
                  component={Paper}
                  style={{
                    overflowX: 'scroll',
                    boxShadow: '0px 0px 0px 1px #E0E0E0',
                    borderRadius: 4,
                    flex: 1,
                    flexGrow: 1,
                  }}
                >
                  <TableWrapper
                    stickyHeader
                    style={{
                      borderRadius: 4,
                      height: '100%',
                      borderCollapse: 'collapse',
                    }}
                  >
                    <TableHead
                      style={{
                        position: 'sticky',
                        top: -1,
                        zIndex: 2,
                      }}
                    >
                      <TableRow>
                        {columns.map((column: string, index: number) => {
                          const sortField = campaignTableColumnToSortBy(
                            column,
                            group.value
                          );
                          const isSortable = sortField !== null;
                          const headerStyle =
                            index === 0
                              ? {
                                  minWidth: 300,
                                  padding: '4px 10px',
                                  borderRadius: 0,
                                  border: '1px solid #F0F0F1',
                                  borderBottom: 'none',
                                  background: '#F8F8F8',
                                }
                              : {
                                  minWidth: ['Aligned', 'Impressions'].includes(
                                    column
                                  )
                                    ? 120
                                    : 220,
                                  padding: '4px 10px',
                                  borderRadius: 0,
                                  border: '1px solid #F0F0F1',
                                  borderBottom: 'none',
                                  background: '#F8F8F8',
                                };

                          return (
                            <HeaderCell
                              key={column}
                              sortDirection={
                                sortField !== null && rowsSortBy === sortField
                                  ? rowsSortOrder
                                  : false
                              }
                              style={headerStyle}
                            >
                              {isSortable ? (
                                <TableSortLabel
                                  active={rowsSortBy === sortField}
                                  direction={
                                    rowsSortBy === sortField
                                      ? rowsSortOrder
                                      : 'asc'
                                  }
                                  onClick={() =>
                                    handleCampaignRowsSort(sortField)
                                  }
                                >
                                  {column}
                                </TableSortLabel>
                              ) : (
                                <span>{column}</span>
                              )}
                            </HeaderCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    {itemsLoading ? (
                      <TableRow style={{ height: '100%', border: 'none' }}>
                        <TableCell colSpan={columns.length}>
                          <Spinner />
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableBody>
                        {group.value === 'episode'
                          ? items.map((item, index) => (
                              <CampaignItemEpisodeRow
                                key={`${item.id}-${index}`}
                                columns={columns}
                                garmScoreColumns={garmScoreColumns}
                                data={{
                                  ...item,
                                  alignmentCode: item.alignmentCode,
                                }}
                                onClick={(showName, episodeName) => {
                                  fetchGarmScoresPerShow(showName, episodeName);
                                }}
                              />
                            ))
                          : showItems.map((data, index) => {
                              const item = data.show;
                              const newItem = {
                                ...item,
                                iabCategories:
                                  item.iabCategories &&
                                  Object.keys(item.iabCategories)
                                    .map((key) => ({
                                      count: item.iabCategories[key],
                                      name: key,
                                    }))
                                    .sort((a: any, b: any) => b.count - a.count)
                                    .slice(0, 3),
                                show: decode(item.show),
                                showOnDB: item.show,
                                listenerCount: formatPodcastRatingNumber(
                                  item.rating
                                ),
                              };
                              if (
                                item.riskHistories &&
                                item.riskHistories.current
                              ) {
                                Object.keys(item.riskHistories.current).forEach(
                                  (key: string) => {
                                    const historyValues =
                                      item.riskHistories.current[key];
                                    if (historyValues) {
                                      if (key === 'electionsPercentage') {
                                        newItem[key] = {
                                          value: historyValues.medianRiskValue,
                                          up: historyValues.episodeCountHigherThanAverage,
                                          down: historyValues.episodeCountLowerThanAverage,
                                        };
                                      } else {
                                        const score =
                                          historyValues.medianRiskValue;
                                        const oldScore =
                                          item.riskHistories.prev &&
                                          item.riskHistories.prev[key]
                                            ? item.riskHistories.prev[key]
                                                .medianRiskValue
                                            : undefined;
                                        const updatedShow =
                                          score &&
                                          oldScore &&
                                          score !== oldScore;

                                        newItem[key] = {
                                          ...historyValues,
                                          score,
                                          oldScore,
                                          updatedShow,
                                        };
                                      }
                                    }
                                  }
                                );
                              }
                              return (
                                <Row
                                  key={`${item.showId}-${index}`}
                                  name={item.show}
                                  columns={columns}
                                  garmScoreColumns={garmScoreColumns}
                                  data={{
                                    ...newItem,
                                    impressionCount: data.impressionCount,
                                    alignmentCode: data.alignmentCode,
                                  }}
                                  isHost={false}
                                  hostData={{}}
                                  onClick={() => {}}
                                />
                              );
                            })}
                        {/* This final row exists so that the other table rows don't expand in height (and look distorted) if there aren't enough rows to fill the full height of the table */}
                        <TableRow style={{ height: '100%', border: 'none' }}>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    )}
                    <Footer
                      style={{
                        position: 'sticky',
                        bottom: -1,
                        zIndex: 1,
                      }}
                    >
                      <TableRow>
                        <TableCell
                          colSpan={1000}
                          style={{
                            padding: '6px 12px',
                            boxShadow: 'inset 0 7px 2px -7px rgba(0,0,0,0.4)',
                          }}
                        >
                          <Grid
                            container
                            justifyContent='space-between'
                            alignItems='center'
                            style={{
                              position: 'sticky',
                              top: '50%',
                              left: 20,
                              maxWidth: 500,
                            }}
                          >
                            {/* @ts-ignore */}
                            <TablePagination
                              rowsPerPageOptions={[10, 20, 50, 100]}
                              SelectProps={{
                                inputProps: { 'aria-label': 'rows per page' },
                                native: true,
                              }}
                              onPageChange={handleChangePage}
                              onRowsPerPageChange={handleChangeRowsPerPage}
                              count={totalItemsCount}
                              page={page}
                              rowsPerPage={pageSize}
                              style={{
                                paddingTop: 0,
                                paddingBottom: 0,
                                fontSize: 12,
                              }}
                              nextIconButtonProps={{
                                size: 'small',
                              }}
                              backIconButtonProps={{
                                size: 'small',
                              }}
                            />
                          </Grid>
                        </TableCell>
                      </TableRow>
                    </Footer>
                  </TableWrapper>
                </TableContainer>
              </Box>
            </Box>
          )}
        </>
      ) : (
        <NoCampaign onNewCampaign={() => setOpenCreateModal(true)} />
      )}

      <CampaignModal
        open={openCreateModal}
        closeModal={() => setOpenCreateModal(false)}
      />
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby='report-dashboard-modal-title'
        aria-describedby='report-dashboard-modal-description'
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 800,
        }}
      >
        <Box sx={{ ...modalStyle }}>
          <CancelIcon
            className={classes.closeIcon}
            onClick={() => setOpenModal(false)}
          />
          <DashboardList
            title='Select Dashboard'
            dashboards={garmDashboards}
            onSelectDashboard={handleSelectDashboard}
            onChangeFavorite={() => {}}
            selectedDashboardId={null}
            isDashboardSelection
          />
        </Box>
      </Modal>
      <Modal
        open={createNewModal}
        onClose={() => setCreateNewModal(false)}
        aria-labelledby='report-graph-modal-title'
        aria-describedby='report-graph-modal-description'
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 800,
        }}
      >
        <Box sx={{ ...createNewModalStyle }}>
          <CancelIcon
            className={classes.closeIcon}
            onClick={() => setCreateNewModal(false)}
          />
          <Typography variant='body1' style={{ fontSize: 14 }}>
            Dashboard Name
          </Typography>
          <TextInput
            default='Enter name'
            value={newDashboardName}
            onChange={(name) => setNewDashboardName(name)}
            className={classes.description}
          />
          <Box
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              title='Cancel'
              variant='outlined'
              height={36}
              onClick={() => setCreateNewModal(false)}
            />
            <Button
              disabled={!newDashboardName}
              title='Create'
              height={36}
              onClick={() => {
                handleNewDashboard(newDashboardName);
              }}
            />
          </Box>
        </Box>
      </Modal>
      <Modal
        open={submitScoringModalOpen}
        onClose={closeSubmitScoringModal}
        aria-labelledby='submit-scoring-modal-title'
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 800 }}
      >
        <Box
          sx={{
            ...createNewModalStyle,
            position: 'relative',
            maxWidth: 480,
          }}
        >
          <CancelIcon
            className={classes.closeIcon}
            onClick={closeSubmitScoringModal}
          />
          <Typography
            id='submit-scoring-modal-title'
            variant='body1'
            style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}
          >
            Submit scoring
          </Typography>
          <Typography
            className={textClasses.smRegular}
            style={{ color: '#475467', marginBottom: 8 }}
          >
            <b>Total Unscored Episodes:</b>{' '}
            {formatNumber(unscoredScoringTotals.episodes)}
          </Typography>
          <Typography
            className={textClasses.smRegular}
            style={{ color: '#475467', marginBottom: 16 }}
          >
            <b>Total Unscored Impressions:</b>{' '}
            {formatNumber(unscoredScoringTotals.impressions)}
          </Typography>
          <Typography
            className={textClasses.smRegular}
            style={{ color: '#344054', marginBottom: 8 }}
          >
            Select episodes to score
          </Typography>
          <Box paddingX={2}>
            <Slider
              className={classes.scoringEpisodesSlider}
              value={[
                Math.min(scoringEpisodesSlider, SCORING_EPISODES_SLIDER_MAX),
                scoringEpisodesSliderMax,
              ]}
              onChange={(_, values) => {
                const newValues = values as number[];
                setScoringEpisodesSlider(
                  Math.min(newValues[0], SCORING_EPISODES_SLIDER_MAX)
                );
              }}
              min={0}
              max={Math.max(unscoredScoringTotals.episodes, 0)}
              step={1}
              disabled={unscoredScoringTotals.episodes === 0}
              marks={
                unscoredScoringTotals.episodes === 0
                  ? [{ value: 0, label: '0' }]
                  : unscoredScoringTotals.episodes > SCORING_EPISODES_SLIDER_MAX
                  ? [
                      { value: 0, label: '0' },
                      {
                        value: SCORING_EPISODES_SLIDER_MAX,
                        label: SCORING_EPISODES_SLIDER_MAX + '',
                      },
                      {
                        value: unscoredScoringTotals.episodes,
                        label: formatNumber(unscoredScoringTotals.episodes),
                      },
                    ]
                  : [
                      { value: 0, label: '0' },
                      {
                        value: unscoredScoringTotals.episodes,
                        label: formatNumber(unscoredScoringTotals.episodes),
                      },
                    ]
              }
            />
          </Box>
          <Typography
            className={textClasses.smRegular}
            style={{ color: '#475467', marginTop: 12 }}
          >
            <b>Episodes to score:</b> {formatNumber(scoringEpisodesSlider)}
          </Typography>
          <Typography
            className={textClasses.smRegular}
            style={{ color: '#475467', marginBottom: 16 }}
          >
            <b>Impressions to score:</b>{' '}
            {formatNumber(unscoredImpressionsToScore)}
          </Typography>
          <Box
            display='flex'
            flexDirection='row'
            alignItems='center'
            justifyContent='center'
            style={{ gap: 12, marginBottom: 16, width: '100%' }}
          >
            <Typography variant='body1' style={{ fontSize: 14, flexShrink: 0 }}>
              Authorization Code
            </Typography>
            <TextInput
              default='Enter authorization code'
              value={scoringAuthorizationCode}
              onChange={(v) => setScoringAuthorizationCode(v)}
              className={classes.description}
              style={{ width: 260, maxWidth: '100%' }}
            />
          </Box>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              marginTop: 20,
            }}
          >
            <Button
              className={classes.addBtn}
              height={36}
              disabled={
                submitScoringSubmitting ||
                !selectedCampaignId ||
                !scoringAuthorizationCode.trim()
              }
              onClick={handleSubmitScoringProcess}
            >
              {submitScoringSubmitting ? (
                <Spinner size={12} />
              ) : (
                'Submit Scoring Process'
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Dialog open={!!currentCampaign} fullScreen>
        <GarmArticleContent
          episode={currentCampaign}
          show={currentShow || ''}
          showGenres={[]}
          onClose={() => {
            setCurrentCampaign(null);
            setCurrentShow(null);
          }}
        />
      </Dialog>
    </>
  );
}
